import { useReducer, useContext, useState } from 'react';
import { Active, Over } from '@dnd-kit/core';
import {
    EditorUIElement,
    EditorUIContainer,
    UILayout,
    Description,
    VisibilityTypeId,
    UIArTableColumnOverride,
    WorkflowMenuSection,
} from '#types/System.types';
import { UserContext } from '../../providers/UserContext';
import { layoutEditorReducer, initialLayoutEditorState } from '../../reducers/layoutEditorReducer';
import { normalizeLayoutColumnConfig } from '../../components/editor/columnConfigNormalization';

/**
 * Mode for the layout editor - editing the layout, bulk editing fields, editing
 * details (rules) or authoring derived values. `Derives` is form-only, and its
 * pane writes through the same reducer history as every other mode.
 * @typedef {'Layout' | 'BulkEdit' | 'Details' | 'Derives'} LayoutEditorMode
 */
export type LayoutEditorMode = 'Layout' | 'BulkEdit' | 'Details' | 'Derives';

/**
 * Preview mode for the layout editor
 * @typedef {'off' | 'desktop'} PreviewMode
 */
export type PreviewMode = 'off' | 'desktop';

/**
 * Props for the useLayoutEditor hook
 * @interface UseLayoutEditorProps
 * @property {function} [onSave] - Optional callback function to save the layout
 * @property {UILayout} onSave.layout - The layout to save
 * @property {string} onSave.userId - The user ID
 * @property {Description} onSave.description - The layout description
 * @returns {Promise<void>}
 */
interface UseLayoutEditorProps {
    onSave?: (layout: UILayout, description: Description, state?: any) => Promise<void>;
}

/**
 * Hook for managing the layout editor state and operations
 * This files is the base for the layout editor and is used by the entity and form layout editors `useLayoutEditorEntity` and `useLayoutEditorForm`
 * @param {UseLayoutEditorProps} props - The hook props
 * @returns {Object} Layout editor state and methods
 * @property {UILayout} layout - Current layout state
 * @property {EditorUIElement | null} selectedElement - Currently selected element
 * @property {boolean} isModified - Whether the layout has unsaved changes
 * @property {Description} description - Layout description
 * @property {function} selectElement - Select an element in the editor
 * @property {function} removeElement - Remove an element from a container
 * @property {function} addElement - Add an element to a container
 * @property {function} dropElement - Handle element drop during drag and drop
 * @property {function} undo - Undo last action
 * @property {function} redo - Redo last undone action
 * @property {function} save - Save the current layout, returns a Promise
 * @property {Dispatch<any>} dispatch - Reducer dispatch function
 * @property {LayoutEditorMode} mode - Current editor mode
 * @property {function} setMode - Set editor mode
 * @property {PreviewMode} previewMode - Current preview mode
 * @property {function} setPreviewMode - Set preview mode
 * @property {string} previewVisibilityMode - Current preview visibility mode
 * @property {function} setPreviewVisibilityMode - Set preview visibility mode
 * @property {VisibilityTypeId|undefined} visibilityMatrixFocus - View the visibility matrix is focused on; deliberately outside the reducer so it never rides inside an undo snapshot
 * @property {function} setVisibilityMatrixFocus - Set the focused view
 * @property {string|undefined} visibilityMatrixRow - Element the visibility matrix is focused on, by `dndId`; deliberately outside the reducer so it never rides inside an undo snapshot. Set only by `Open in matrix`, and cleared by `LayoutEditorToolbar` on every mode switch made by hand, so a stale row can never re-highlight a later visit to Bulk Edit
 * @property {function} setVisibilityMatrixRow - Set the focused element row
 * @property {LayoutEditorState} layoutEditorState - Complete editor state
 */
export const useLayoutEditorBase = ({ onSave }: UseLayoutEditorProps) => {
    const [state, dispatch] = useReducer(layoutEditorReducer, initialLayoutEditorState);
    const [mode, setMode] = useState<LayoutEditorMode>('Layout');
    const [previewMode, setPreviewMode] = useState<PreviewMode>('off');
    const [previewVisibilityMode, setPreviewVisibilityMode] = useState<string>('internal');
    const [visibilityMatrixFocus, setVisibilityMatrixFocus] = useState<VisibilityTypeId | undefined>(undefined);
    const [visibilityMatrixRow, setVisibilityMatrixRow] = useState<string | undefined>(undefined);
    const { user } = useContext(UserContext);

    const selectElement = (element: EditorUIElement | null) => {
        dispatch({ type: 'SELECT_ELEMENT', payload: element });
    };

    const removeElement = (
        container: EditorUIContainer, 
        elementDndId: string
    ) => {
        dispatch({ 
            type: 'REMOVE_ELEMENT', 
            payload: { container, elementDndId } 
        });
    };

    const addElement = (
        element: EditorUIElement, 
        container: EditorUIContainer
    ) => {
        dispatch({ 
            type: 'ADD_ELEMENT', 
            payload: { element, container } 
        });
    };

    const dropElement = (active: Active, over: Over) => {
        dispatch({ 
            type: 'DROP_ELEMENT', 
            payload: { active, over } 
        });
    };

    const undo = () => {
        dispatch({ type: 'UNDO' });
    };

    const redo = () => {
        dispatch({ type: 'REDO' });
    };

    const save = async () => {
        if (!user?.uid) {
            throw new Error('User not found');
        }
        const normalizedLayout = normalizeLayoutColumnConfig(state.layout);
        await onSave?.({
            ...normalizedLayout,
        }, state.description, state);
        dispatch({ type: 'SAVE_COMPLETE' });
    };

    const updateRequiredState = (elementDndId: string, required: boolean) => {
        dispatch({
            type: 'UPDATE_FIELD_PROPERTY',
            payload: {
                elementDndId,
                property: 'required',
                value: required
            }
        });
    };

    const updateVisibilityTypes = (elementDndId: string, visibilityTypes: VisibilityTypeId[] | undefined) => {
        dispatch({
            type: 'UPDATE_VISIBILITY_TYPES',
            payload: {
                elementDndId,
                visibilityTypes
            }
        });
    };

    const updateAlwaysRequiredState = (elementDndId: string, alwaysRequired: boolean) => {
        dispatch({
            type: 'UPDATE_ALWAYS_REQUIRED',
            payload: {
                elementDndId,
                alwaysRequired
            }
        });
    };

    const updateInternalOnly = (elementDndId: string, internalVisibilityTag: boolean) => {
        dispatch({
            type: 'UPDATE_INTERNAL_ONLY',
            payload: {
                elementDndId,
                internalVisibilityTag
            }
        });
    };

    const updateTriggerSearchSync = (elementDndId: string, triggerSearchSync: boolean) => {
        dispatch({
            type: 'UPDATE_TRIGGER_SEARCH_SYNC',
            payload: {
                elementDndId,
                triggerSearchSync
            }
        });
    };

    const updateButtonConfig = (
        elementDndId: string,
        patch: { menuSection?: WorkflowMenuSection; menuItemKey?: string; requiresConfirmation?: boolean; label?: string }
    ) => {
        dispatch({
            type: 'UPDATE_BUTTON_CONFIG',
            payload: {
                elementDndId,
                ...patch
            }
        });
    };

    const bulkUpdateRequired = (updates: Array<{ elementDndId: string; required: boolean }>) => {
        dispatch({
            type: 'BULK_UPDATE_REQUIRED',
            payload: { updates }
        });
    };

    const bulkUpdateVisibility = (
        updates: Array<{ elementDndId: string; visibilityTypes: VisibilityTypeId[] | undefined }>
    ) => {
        dispatch({
            type: 'BULK_UPDATE_VISIBILITY',
            payload: { updates }
        });
    };

    const updateProtected = (fieldId: string | undefined, entityReferenceId: string | undefined, isProtected: boolean) => {
        dispatch({
            type: 'UPDATE_PROTECTED',
            payload: { fieldId, entityReferenceId, isProtected }
        });
    };

    const updateColumnConfig = (elementDndId: string, columnConfig: UIArTableColumnOverride[] | undefined) => {
        dispatch({
            type: 'UPDATE_COLUMN_CONFIG',
            payload: { elementDndId, columnConfig }
        });
    };

    return {
        layout: state.layout,
        selectedElement: state.selectedElement,
        isModified: state.history.past.length > 0,
        canUndo: state.history.past.length > 0,
        canRedo: state.history.future.length > 0,
        description: state.description,
        selectElement,
        removeElement,
        addElement,
        dropElement,
        undo,
        redo,
        save,
        dispatch,
        mode,
        setMode,
        previewMode,
        setPreviewMode,
        previewVisibilityMode,
        setPreviewVisibilityMode,
        visibilityMatrixFocus,
        setVisibilityMatrixFocus,
        visibilityMatrixRow,
        setVisibilityMatrixRow,
        user,
        layoutEditorState: state,
        updateRequiredState,
        updateVisibilityTypes,
        updateAlwaysRequiredState,
        updateInternalOnly,
        updateTriggerSearchSync,
        updateButtonConfig,
        bulkUpdateRequired,
        bulkUpdateVisibility,
        updateProtected,
        updateColumnConfig
    };
}; 